import { Maybe } from '@csp/csp-common-model';
import { BioSampleCodeFormatType, BioSampleContainer } from '@csp/csp-common-hbs-model';
import { isDefined } from '@csp/csp-common-util';
import { ScheduleVisitTexts } from '@csp/csp-common-visits';
import { BioSampleItem } from '../../model/BioSampleItem';
import { BioSampleKitItem } from '../model/BioSampleKitItem';
import { BioSampleContainerItemMapper } from '../../mapper/BioSampleContainerItemMapper';
import { BioSampleContainerItem } from '../../model/BioSampleContainerItem';
import { KitDetails } from '../model/KitDetails';

const toKitDetails = (containers: BioSampleContainer[]): Maybe<KitDetails> => {
  const firstFoundContainer = containers[0];
  if (firstFoundContainer) {
    const { kitCode, kitType, kitDescription, kitExpirationDateStr } = firstFoundContainer;
    return { kitCode, kitType, kitDescription, kitExpirationDateStr };
  } else {
    return undefined;
  }
};

const toBioSampleContainerItem = ({
  container,
  sampleItems,
  supportedBioSampleIdFormats,
}: {
  container: BioSampleContainer;
  sampleItems: BioSampleItem[];
  supportedBioSampleIdFormats: BioSampleCodeFormatType[];
}): Maybe<BioSampleContainerItem> => {
  const matchingBioSampleItem = sampleItems.find(
    bioSampleItem =>
      bioSampleItem.kitCode === container.kitCode && bioSampleItem.containerNumber === container.containerNumber,
  );

  return BioSampleContainerItemMapper.toBioSampleContainerItem({
    container,
    bioSampleItem: matchingBioSampleItem,
    formats: supportedBioSampleIdFormats,
  });
};

const toBioSampleContainerItems = ({
  containers,
  sampleItems,
  supportedBioSampleIdFormats,
}: {
  containers: BioSampleContainer[];
  sampleItems: BioSampleItem[];
  supportedBioSampleIdFormats: BioSampleCodeFormatType[];
}): BioSampleContainerItem[] =>
  containers
    .map(container => toBioSampleContainerItem({ container, sampleItems, supportedBioSampleIdFormats }))
    .filter(isDefined)
    .sort((a, b) => a.containerNumber.localeCompare(b.containerNumber, undefined, { numeric: true }));

const toKitItem = ({
  kitDetails,
  containers,
  sampleItems,
  supportedBioSampleIdFormats,
  supportedVisits,
}: {
  kitDetails: KitDetails;
  containers: BioSampleContainer[];
  sampleItems: BioSampleItem[];
  supportedBioSampleIdFormats: BioSampleCodeFormatType[];
  supportedVisits: ScheduleVisitTexts[];
}): BioSampleKitItem => ({
  kitDetails,
  kitContainers: toBioSampleContainerItems({ containers, sampleItems, supportedBioSampleIdFormats }),
  supportedVisits,
});

export const BioSampleKitItemMapper = {
  toKitDetails,
  toKitItem,

  // Exposed for testing purposes
  toBioSampleContainerItems,
};
